import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Visibility,
  VisibilityOff,
  LocationOn,
  Phone,
  Search as SearchIcon,
} from "@mui/icons-material";
import { completedFreights, partyList } from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebounce } from "../../../Hooks/useDebounce";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// ── Dialog Header ──────────────────────────────────────────────────
const DialogHeader = ({ title, onClose }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography sx={{ fontWeight: 700, color: T.white, fontSize: "0.88rem" }}>
      {title}
    </Typography>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{
        color: "rgba(255,255,255,0.8)",
        p: 0.5,
        ...sq,
        "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
      }}
    >
      <CloseIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Box>
);

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({ tip, icon, isDelete, isEdit, isView, onClick }) => {
  const c = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : T.primary;

  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : T.primaryBg;

  const bd = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : T.border;

  const hBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          sx={{
            width: 20,
            height: 20,
            ...sq,
            color: c,
            bgcolor: bg,
            border: `1px solid ${bd}`,
            transition: "all 0.18s",
            "&:hover": {
              bgcolor: hBg,
              borderColor: c,
              transform: "translateY(-1px)",
            },
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 12 } })}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1, sm: 1.5 },
      textAlign: "center",
      ...sq,
      border: `1.5px solid ${color}40`,
      borderTop: `3px solid ${color}`,
      bgcolor: `${color}08`,
      boxShadow: "none",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.55rem", sm: "0.65rem" },
        color: T.textSub,
        lineHeight: 1.2,
        mb: 0.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.72rem", sm: "1.1rem" },
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {value}
    </Typography>
  </Paper>
);

// ── MobileFreightCard ──────────────────────────────────────────────
const MobileFreightCard = ({
  row,
  idx,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}) => {
  const DataPair = ({ label, value, color, xs = 6 }) => (
    <Grid item xs={xs}>
      <Typography
        sx={{
          fontSize: "0.58rem",
          color: T.textMuted,
          fontWeight: 600,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.7rem", fontWeight: 600, color: color || T.text }}
      >
        {value ?? "—"}
      </Typography>
    </Grid>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 60,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : T.successBg,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 20, color: expanded ? T.white : T.success }}
          />
          <Typography
            sx={{
              fontSize: "0.52rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.truck_no || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textSub,
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mb: 0.25,
            }}
          >
            {row.party_name || "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <LocationOn sx={{ fontSize: 10, color: T.textMuted }} />
            <Typography
              sx={{
                fontSize: "0.62rem",
                color: T.textMuted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.from_location || "—"} → {row.to_location || "—"}
            </Typography>
          </Box>
          <Chip
            label="Completed"
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: 10 }} />}
            sx={{
              mt: 0.4,
              width: "fit-content",
              ...sq,
              fontSize: "0.55rem",
              height: 16,
              bgcolor: T.successBg,
              color: T.success,
              border: `1px solid ${T.successBorder}`,
              "& .MuiChip-label": { px: 0.75 },
              "& .MuiChip-icon": { color: T.success, ml: 0.5 },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1.2,
            py: 1,
            minWidth: 100,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.55rem", color: T.textMuted, fontWeight: 600 }}
            >
              Net Payable
            </Typography>
            <Typography
              sx={{ fontWeight: 800, fontSize: "0.8rem", color: T.primary }}
            >
              ₹{row.net_payable || 0}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 26,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        </Box>
      </Box>

      {expanded && (
        <Box
          sx={{
            bgcolor: T.primaryBg,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1.25}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                📋 Basic Info
              </Typography>
              <Grid container spacing={0.5}>
                <DataPair
                  label="Loading"
                  value={formatDate(row.loading_date)}
                />
                <DataPair
                  label="Unloading"
                  value={formatDate(row.unloading_date)}
                  xs={12}
                />
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                ⚖️ Weight
              </Typography>
              <Grid container spacing={0.5}>
                <DataPair label="Load" value={row.loading_wt || 0} xs={3} />
                <DataPair label="Recv" value={row.received_wt || 0} xs={3} />
                <DataPair
                  label="Short"
                  value={row.shortage_wt || 0}
                  color={T.red}
                  xs={3}
                />
                <DataPair label="Bill" value={row.billing_wt || 0} xs={3} />
              </Grid>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💰 Charges
              </Typography>
              <Grid container spacing={0.5}>
                {[
                  ["Rate", `₹${row.freight_rate || 0}`],
                  ["LR", `₹${row.freight_amt || 0}`],
                  ["TDS", `₹${row.tds_amt || 0}`],
                  ["Shortage", `₹${row.shortage_amt || 0}`],
                  ["Commission", `₹${row.commission || 0}`],
                  ["Other", `₹${row.other_exp || 0}`],
                ].map(([l, v]) => (
                  <DataPair key={l} label={l} value={v} />
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                p: 1,
                bgcolor: T.warningBg,
                border: `1px solid ${T.warningBorder}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.warning,
                  mb: 0.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💵 Advance
              </Typography>
              <Grid container spacing={0.5}>
                <DataPair
                  label="Mines"
                  value={`₹${row.ad_mines_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Diesel"
                  value={`₹${row.ad_diesel_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Cash"
                  value={`₹${row.ad_cash_exp || 0}`}
                  xs={4}
                />
                <DataPair
                  label="Total Advance"
                  value={`₹${row.total_advance || 0}`}
                  color={T.warning}
                  xs={12}
                />
              </Grid>
            </Box>

            {(row.truck_owner || row.phone_no || row.pan_no) && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  👤 Owner
                </Typography>
                <Grid container spacing={0.5}>
                  {row.truck_owner && (
                    <DataPair label="Owner" value={row.truck_owner} xs={12} />
                  )}
                  {row.phone_no && (
                    <DataPair label="Phone" value={row.phone_no} />
                  )}
                  {row.pan_no && <DataPair label="PAN" value={row.pan_no} />}
                </Grid>
              </Box>
            )}

            <Box
              sx={{
                p: 1,
                bgcolor: T.primaryLight,
                border: `1.5px solid ${T.border}`,
                ...sq,
              }}
            >
              <Grid container spacing={0.5}>
                <DataPair
                  label="Net Amount"
                  value={`₹${row.net_amount || 0}`}
                />
                <DataPair
                  label="Net Payable"
                  value={`₹${row.net_payable || 0}`}
                  color={T.primary}
                />
                <DataPair
                  label="Paid"
                  value={`₹${row.paid_amount?.toFixed(1) || 0}`}
                  color={T.success}
                />
                <DataPair
                  label="Balance"
                  value={`₹${row.bal_amount?.toFixed(1) || 0}`}
                  color={T.red}
                />
              </Grid>
            </Box>

            {row.remark && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    color: T.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Remark
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", color: T.text, fontWeight: 500 }}
                >
                  {row.remark}
                </Typography>
              </Box>
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(row, idx)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
              onClick={() => onDelete(idx)}
              sx={{ ...dangerBtn, height: 30, px: 1.5 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
export default function CompletedFreight() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [data, setData] = useState([]);
  const [partyFilter, setPartyFilter] = useState("");
  const [tpFilter, setTpFilter] = useState("");
  const [truckFilter, setTruckFilter] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState({});
  const [editIndex, setEditIndex] = useState(null);
  const [partyOptions, setParties] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [lrNumberFilter, setLrNumberFilter] = useState("");

  const debouncedParty = useDebounce(partyFilter, 500);
  const debouncedTruck = useDebounce(truckFilter, 500);
  const debouncedLrNumber = useDebounce(lrNumberFilter, 500);
  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  const hasActiveFilters = Boolean(
    partyFilter?.trim() || truckFilter?.trim() || lrNumberFilter?.trim(),
  );

  const fetchCompletedFreights = useCallback(
    async (pageNo = 1, filters = {}) => {
      if (loading) return;
      setLoading(true);
      try {
        const response = await completedFreights(pageNo, filters);
        const results = response?.results || [];
        if (pageNo === 1) {
          setData(results);
          setPage(2);
        } else {
          setData((prev) => [...prev, ...results]);
          setPage(pageNo + 1);
        }
        setHasMore(!!response.next);
      } catch (error) {
        console.error("Error fetching completed freights:", error);
      } finally {
        setLoading(false);
      }
    },
    [loading],
  );

  useEffect(() => {
    setData([]);
    setPage(1);
    setExpandedCardId(null);
    fetchCompletedFreights(1, {
      party_name: debouncedParty || undefined,
      truck_no: debouncedTruck || undefined,
      lr_number: debouncedLrNumber || undefined,
    });
  }, [debouncedParty, debouncedTruck, debouncedLrNumber]);

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const res = await partyList({ party_type: "consignor" });
        setParties(Array.isArray(res) ? res : []);
      } catch (error) {
        console.error("Failed to fetch parties:", error);
        setParties([]);
      }
    };
    fetchParties();
  }, []);
  const fetchMoreData = () => {
    if (!loading && hasMore)
      fetchCompletedFreights(page, {
        party_name: debouncedParty || undefined,
        truck_no: debouncedTruck || undefined,
        lr_number: debouncedLrNumber || undefined,
      });
  };

  const handleDelete = (index) => {
    setData((prev) => prev.filter((_, i) => i !== index));
    setSnackbar({
      open: true,
      message: "Record deleted successfully",
      severity: "success",
    });
  };

  const handleEditOpen = (row, index) => {
    setEditRow(row);
    setEditIndex(index);
    setEditDialogOpen(true);
  };
  const handleEditChange = (field, value) =>
    setEditRow({ ...editRow, [field]: value });

  const handleEditSave = () => {
    const updated = [...data];
    updated[editIndex] = editRow;
    setData(updated);
    setEditDialogOpen(false);
    setSnackbar({
      open: true,
      message: "Record updated successfully",
      severity: "success",
    });
  };

  const clearFilters = () => {
    setPartyFilter("");
    setTruckFilter("");
    setLrNumberFilter("");
  };

  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");

    const headers = [
      "Sr No",
      "LR ID",
      "Party Name",
      "Loading Date",
      "Unloading Date",
      "Truck No.",
      "D.O. No.",
      "T.P. No.",
      "From",
      "To",
      "Loading Wt",
      "Receiving Wt",
      "Shortage Wt",
      "Billing Wt",
      "LR Rate",
      "LR Amt",
      "TDS %",
      "TDS Amt",
      "Shortage Rate",
      "Shortage Amt",
      "Ad Mines",
      "Ad Diesel",
      "Ad Cash",
      "Total Ad",
      "Commission",
      "Net Amt",
      "Others Exp.",
      "Net Payable",
      "Paid Amt",
      "Bal Amt",
      "Truck Owner",
      "Phone No.",
      "PAN No.",
      "Remark",
      "Date",
    ];

    const rows = data.map((row, i) => [
      i + 1,
      row.freight_id ?? "—",
      row.party_name || "—",
      formatDate(row.loading_date),
      formatDate(row.unloading_date),
      row.truck_no || "—",
      row.do_no || "—",
      row.tp_no || "—",
      row.from_location || "—",
      row.to_location || "—",
      row.loading_wt ?? "—",
      row.received_wt ?? "—",
      row.shortage_wt ?? "—",
      row.billing_wt ?? "—",
      row.freight_rate ?? "—",
      row.freight_amt ?? "—",
      row.tds_per ?? "—",
      row.tds_amt ?? "—",
      row.shortage_rate ?? "—",
      row.shortage_amt ?? "—",
      row.ad_mines_exp ?? "—",
      row.ad_diesel_exp ?? "—",
      row.ad_cash_exp ?? "—",
      row.total_advance ?? "—",
      row.commission ?? "—",
      row.net_amount ?? "—",
      row.other_exp ?? "—",
      row.net_payable ?? "—",
      row.paid_amount ?? "—",
      row.bal_amount ?? "—",
      row.truck_owner || "—",
      row.phone_no || "—",
      row.pan_no || "—",
      row.remark || "—",
      formatDate(row.created_at),
    ]);

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));
      return { wch: maxLen + 4 };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Completed LR's",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "Completed LR's");
    XLSX.writeFile(
      wb,
      `Completed_Freights_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>Completed LR's</title>
      <style>body{font-family:Arial;padding:20px;}h2,h4{text-align:center;}
      table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ddd;padding:8px;text-align:left;}
      th{background-color:#f5f5f5;}</style></head>
      <body><h2>PARAS ROAD LINES</h2><h4>Completed LR's</h4>
      <table><thead><tr><th>S.No</th><th>LR ID</th><th>Party Name</th><th>Truck No</th><th>Loading Date</th><th>Net Payable</th></tr></thead>
      <tbody>${data.map((row, i) => `<tr><td>${i + 1}</td><td>${row.freight_id || "-"}</td><td>${row.party_name || "-"}</td><td>${row.truck_no || "-"}</td><td>${formatDate(row.loading_date)}</td><td>₹${row.net_payable || 0}</td></tr>`).join("")}
      </tbody></table></body></html>`);
    w.document.close();
    w.print();
    w.close();
  };

  const partyNameOptions = partyOptions.map((o) => o.name);
  const truckOptions = [
    ...new Set(data.map((i) => i.truck_no).filter(Boolean)),
  ];

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -2, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          <Grid item xs={12} sm={4} md={2.5}>
            <TextField
              size="small"
              placeholder="LR No."
              fullWidth
              value={lrNumberFilter}
              onChange={(e) => setLrNumberFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
                endAdornment: lrNumberFilter && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setLrNumberFilter("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 12, color: T.textMuted }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>
          {/* Party */}
          <Grid item xs={12} sm={4} md={3}>
            <Autocomplete
              freeSolo
              options={partyNameOptions}
              value={partyFilter}
              onInputChange={(_, v) => setPartyFilter(v || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Party Name"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Truck */}
          <Grid item xs={12} sm={4} md={2.5}>
            <Autocomplete
              freeSolo
              options={truckOptions}
              value={truckFilter}
              onInputChange={(_, v) => setTruckFilter(v || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Truck No."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <TruckIcon sx={{ fontSize: 14, color: T.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={filterInputSx}
                />
              )}
            />
          </Grid>

          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear All Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  sx={{
                    ...dangerBtn,
                    height: 34,
                    minWidth: 34,
                    px: 1,
                    "&.Mui-disabled": {
                      borderColor: T.border,
                      bgcolor: T.white,
                      color: T.textMuted,
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Print */}
          <Grid item xs="auto">
            <Tooltip title="Print" arrow>
              <Button
                size="small"
                onClick={handlePrint}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <PrintIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>

          {/* Export */}
          <Grid item xs="auto">
            <Tooltip title="Export to Excel" arrow>
              <Button
                size="small"
                onClick={handleExportExcel}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <DownloadIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* ── Mobile / Table ── */}
      {isMobile ? (
        loading && data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.primaryBg,
              ...sq,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              No completed freights found
            </Typography>
          </Box>
        ) : (
          <Box
            id="completed-mobile"
            sx={{ maxHeight: "calc(100vh - 280px)", overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="completed-mobile"
            >
              {data.map((row, idx) => (
                <MobileFreightCard
                  key={row.freight_id || idx}
                  row={row}
                  idx={idx}
                  expanded={expandedCardId === (row.freight_id || idx)}
                  onToggle={() => handleCardToggle(row.freight_id || idx)}
                  onEdit={handleEditOpen}
                  onDelete={handleDelete}
                />
              ))}
            </InfiniteScroll>
          </Box>
        )
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...sq,
            border: `1.5px solid ${T.border}`,
            overflow: "hidden",
            boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            height: "calc(100vh - 150px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TableContainer
            id="completed-freight-container"
            sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
          >
            <InfiniteScroll
              dataLength={data.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <CircularProgress size={20} sx={{ color: T.primary }} />
                </Box>
              }
              scrollableTarget="completed-freight-container"
              style={{ overflow: "visible" }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 2000,
                  borderCollapse: "collapse",
                  "& td, & th": {
                    py: 0,
                    px: 0.75,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    height: 28,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                  },
                  "& td": {
                    height: 26,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      { l: "#", sticky: true, left: 0, w: 35 },
                      { l: "LR No", sticky: true, left: 35, w: 100 },
                      { l: "Truck", sticky: true, left: 135, w: 120 },
                      { l: "Party", w: 120 },
                      { l: "Loading", w: 85 },
                      { l: "Unloading", w: 85 },
                      { l: "From", w: 90 },
                      { l: "To", w: 90 },
                      { l: "Owner", w: 100 },
                      { l: "Phone", w: 80 },
                      { l: "Remark", w: 100 },
                      { l: "Date", w: 90 },
                      { l: "Actions", sticky: true, right: 0, w: 75 },
                    ].map(({ l, sticky, left, right, w }) => (
                      <TableCell
                        key={l}
                        align="center"
                        sx={{
                          minWidth: w,
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                          textAlign: "center !important",
                          ...(sticky && {
                            position: "sticky",
                            ...(left !== undefined && { left }),
                            ...(right !== undefined && { right }),
                            zIndex: 12,
                          }),
                        }}
                      >
                        {l}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading && data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CircularProgress sx={{ color: T.primary }} size={32} />
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={13}
                        sx={{ textAlign: "center", py: 6, border: "none" }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: 40,
                            color: T.border,
                            mb: 1,
                            display: "block",
                            mx: "auto",
                          }}
                        />
                        <Typography
                          sx={{
                            color: T.textSub,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                          }}
                        >
                          No records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row, idx) => {
                      const isEven = idx % 2 === 0;
                      const rowBg = isEven ? T.baseRow : T.altRow;
                      const sc = {
                        bgcolor: `${rowBg} !important`,
                        zIndex: 6,
                      };

                      return (
                        <TableRow
                          key={row.freight_id || idx}
                          sx={{
                            "& > td": {
                              bgcolor: rowBg,
                              transition: "background-color 0.12s",
                              textAlign: "center",
                            },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          {/* # */}
                          <TableCell
                            align="center"
                            sx={{
                              position: "sticky",
                              left: 0,
                              bgcolor: `${rowBg} !important`,
                              zIndex: 9,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>

                          {/* LR Number chip */}
                          <TableCell
                            align="center"
                            sx={{
                              position: "sticky",
                              left: 35,
                              bgcolor: `${rowBg} !important`,
                              zIndex: 8,
                              overflow: "hidden",
                              maxWidth: 100,
                            }}
                          >
                            <Chip
                              label={row.lr_number || "—"}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 16,
                                ...sq,
                                bgcolor: T.primaryBg,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          {/* Truck */}
                          <TableCell
                            align="center"
                            sx={{
                              position: "sticky",
                              left: 135,
                              bgcolor: `${rowBg} !important`,
                              zIndex: 7,
                              minWidth: 120, // ← force width
                              maxWidth: 120,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: T.primary,
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.truck_no || "—"}
                            </Typography>
                          </TableCell>

                          {/* Party */}
                          <TableCell align="center">
                            <Tooltip title={row.party_name || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  maxWidth: 120,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  margin: "0 auto",
                                }}
                              >
                                {row.party_name || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell align="center">
                            {formatDate(row.loading_date)}
                          </TableCell>
                          <TableCell align="center">
                            {formatDate(row.unloading_date)}
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title={row.from_location || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  maxWidth: 90,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  margin: "0 auto",
                                }}
                              >
                                {row.from_location || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title={row.to_location || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  maxWidth: 90,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  margin: "0 auto",
                                }}
                              >
                                {row.to_location || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title={row.truck_owner || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  maxWidth: 100,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  margin: "0 auto",
                                }}
                              >
                                {row.truck_owner || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell align="center">
                            {row.phone_no || "—"}
                          </TableCell>

                          <TableCell align="center">
                            <Tooltip title={row.remark || ""} arrow>
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  maxWidth: 100,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  margin: "0 auto",
                                }}
                              >
                                {row.remark || "—"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          {/* Date chip */}
                          <TableCell align="center">
                            <Chip
                              label={formatDate(row.created_at)}
                              size="small"
                              sx={{
                                fontSize: "0.6rem",
                                height: 16,
                                ...sq,
                                bgcolor: T.primaryBg,
                                color: T.primary,
                                border: `1px solid ${T.border}`,
                                fontWeight: 700,
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </TableCell>

                          {/* Actions */}
                          <TableCell
                            align="center"
                            sx={{
                              position: "sticky",
                              right: 0,
                              bgcolor: `${rowBg} !important`,
                              zIndex: 7,
                              py: "2px !important",
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <ActionButton
                                tip="Edit"
                                icon={<EditIcon />}
                                isEdit
                                onClick={() => handleEditOpen(row, idx)}
                              />
                              <ActionButton
                                tip="Delete"
                                icon={<DeleteIcon />}
                                isDelete
                                onClick={() => handleDelete(idx)}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </TableContainer>
        </Paper>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogHeader
          title="Edit LR Record"
          onClose={() => setEditDialogOpen(false)}
        />
        <DialogContent sx={{ p: 2.5, bgcolor: T.primaryBg }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={partyNameOptions}
                value={editRow.party_name || ""}
                onInputChange={(_, v) => handleEditChange("party_name", v)}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="Party Name"
                    size="small"
                    sx={inputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Truck No"
                fullWidth
                size="small"
                value={editRow.truck_no || ""}
                onChange={(e) => handleEditChange("truck_no", e.target.value)}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="From"
                fullWidth
                size="small"
                value={editRow.from_location || ""}
                onChange={(e) =>
                  handleEditChange("from_location", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="To"
                fullWidth
                size="small"
                value={editRow.to_location || ""}
                onChange={(e) =>
                  handleEditChange("to_location", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="LR Amt"
                type="number"
                fullWidth
                size="small"
                value={editRow.freight_amt || ""}
                onChange={(e) =>
                  handleEditChange("freight_amt", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Net Payable"
                type="number"
                fullWidth
                size="small"
                value={editRow.net_payable || ""}
                onChange={(e) =>
                  handleEditChange("net_payable", e.target.value)
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                fullWidth
                multiline
                rows={2}
                size="small"
                value={editRow.remark || ""}
                onChange={(e) => handleEditChange("remark", e.target.value)}
                sx={inputSx}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1,
            bgcolor: T.primaryBg,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Button
            size="small"
            onClick={() => setEditDialogOpen(false)}
            sx={{ ...ghostBtn, height: 32, px: 2 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleEditSave}
            sx={{ ...primaryBtn, height: 32, px: 2.5 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ ...sq, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
